import { Head } from '@inertiajs/react';
import PublicLayout from '@/layouts/public-layout';
import type { StateShowProps } from '@/lib/become-an-agent-types';
import { Hero } from '@/components/become-an-agent/Hero';
import { HostedPath } from '@/components/become-an-agent/HostedPath';
import { StateFactsPanel } from '@/components/become-an-agent/StateFactsPanel';
import { StateBreakdown } from '@/components/become-an-agent/StateBreakdown';
import { BeforeYouSell } from '@/components/become-an-agent/BeforeYouSell';
import { WorkingCredentials } from '@/components/become-an-agent/WorkingCredentials';
import { CostComparison } from '@/components/become-an-agent/CostComparison';
import { TourOperatorWarning } from '@/components/become-an-agent/TourOperatorWarning';
import { SymposiumPhotos } from '@/components/become-an-agent/SymposiumPhotos';
import { AdvisorTestimonials } from '@/components/become-an-agent/AdvisorTestimonials';
import { StateFAQ } from '@/components/become-an-agent/StateFAQ';
import { CrossStateLinks } from '@/components/become-an-agent/CrossStateLinks';
import { PageFooter } from '@/components/become-an-agent/PageFooter';

export default function StateShow({ dataContext, metadata, neighbors = [] }: StateShowProps) {
    const state = dataContext.become;
    if (!state) return null;

    // Per-state SEO meta (description, canonical, OG, Twitter) is rendered
    // server-side from PageController::enrichBecomeAnAgentPage() — that's the
    // single source of truth so Google sees correct tags on initial HTML.
    // Inertia's <Head> only carries the title here (blade dedupes on
    // <title inertia>); duplicating meta children would double-emit tags
    // post-hydration since blade-rendered meta have no head-key to merge.
    // Read title straight from server metadata so `config('seo.guide_year')`
    // is the only place the year is encoded.
    const title =
        (typeof metadata?.title === 'string' ? metadata.title : null) ??
        `How to Become a Travel Agent in ${state.state} | LuxRally Travel`;
    const guideYear = typeof metadata?.seoGuideYear === 'number' ? metadata.seoGuideYear : undefined;

    return (
        <PublicLayout>
            <Head title={title} />
            <Hero state={state} guideYear={guideYear} />
            <HostedPath state={state} />
            <StateFactsPanel state={state} />
            <StateBreakdown state={state} />
            <TourOperatorWarning />
            <BeforeYouSell />
            <SymposiumPhotos />
            <WorkingCredentials />
            <CostComparison state={state} />
            <AdvisorTestimonials />
            <StateFAQ state={state} />
            <CrossStateLinks current={state} neighbors={neighbors} />
            <PageFooter state={state} />
        </PublicLayout>
    );
}
